/**
 * @fileoverview App.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
/**
 * Developed by: Kalindu Tharanga
 */
import { useEffect } from 'react'
import { useLocation, Navigate, Route, Routes } from 'react-router-dom'
import './App.css'
import { useLenis } from './hooks/useLenis'
import { AnimatePresence } from 'framer-motion'
import { PageTransition } from './utils/motion'
import { prefetchApi } from './utils/api'

// Layout Components
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'
import Newsletter from './components/layout/Newsletter'
import ScrollToTop from './components/ui/ScrollToTop'
import SEO from './components/ui/SEO'
import SchemaData from './components/ui/SchemaData'
import WhatsAppButton from './components/ui/WhatsAppButton'
import CookieBanner from './components/ui/CookieBanner'

// Page Components
import Home from './pages/Home'
import TourPlans from './pages/TourPlans'
import TourDetails from './pages/TourDetails'
import FindTour from './pages/FindTour'
import MyTour from './pages/MyTour'
import Experience from './pages/Experience'
import About from './pages/About'
import AboutSriLanka from './pages/AboutSriLanka'
import Contact from './pages/Contact'
import { Privacy, Terms, CookiePolicy, Careers, FAQ, Sitemap } from './pages/SupportPages'
import BookTour from './pages/BookTour'
import CustomTour from './pages/CustomTour'
import WhenToVisit from './pages/WhenToVisit'
import Hotels from './pages/Hotels'
import BlogList from './pages/BlogList'
import BlogPost from './pages/BlogPost'

/**
 * Main Application Component
 * Handles routing and global layout elements.
 */
function App() {
  const location = useLocation();
  useLenis(); // Smooth scroll - runs globally for all pages

  useEffect(() => {
    // Prefetch critical tour and experience data for instant load times
    prefetchApi('/tours?status=active');
    prefetchApi('/experiences');

    // Traffic Logging Logic
    const logTraffic = async () => {
      const hasVisited = localStorage.getItem('kith_visited')
      
      try {
        const type = hasVisited ? 'returning' : 'new'
        const apiUrl = import.meta.env.VITE_API_URL || 'https://engine.kithtravels.com/api'
        
        await fetch(`${apiUrl}/health/traffic`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ type }),
          keepalive: true // Ensures the request finishes even if the user navigates away quickly
        })
        
        localStorage.setItem('kith_visited', 'true')
      } catch (err) {
        console.error("Traffic logging failed:", err)
      }
    }
    logTraffic();
  }, []);

  const hideNavbar = location.pathname.startsWith('/my-tour/');
  const hideFooter = location.pathname.startsWith('/my-tour/');

  return (
    <div className='min-h-screen relative transition-all duration-200 ease-in'>
      <ScrollToTop />
      <SchemaData />
      <WhatsAppButton />
      
      {!hideNavbar && <Navbar />}

      <AnimatePresence mode='wait'>
        <Routes location={location} key={location.pathname}>
          {/* Main Pages */}
          <Route path="/" element={<PageTransition><Home /></PageTransition>} />
          <Route path="/tour-plans" element={<PageTransition><TourPlans /></PageTransition>} />
          <Route path="/tours/:slug" element={<PageTransition><TourDetails /></PageTransition>} />
          <Route path="/blog" element={<PageTransition><BlogList /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
          <Route path="/experience" element={<PageTransition><Experience /></PageTransition>} />
          <Route path="/find-my-tour" element={<PageTransition><FindTour /></PageTransition>} />
          <Route path="/my-tour/:tourId" element={<MyTour />} />
          
          {/* Informational Pages */}
          <Route path="/about-us" element={<PageTransition><About /></PageTransition>} />
          <Route path="/about-sri-lanka" element={<PageTransition><AboutSriLanka /></PageTransition>} />
          <Route path="/contact-us" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/book-tour/:tourId" element={<PageTransition><BookTour /></PageTransition>} />
          <Route path="/custom-tour" element={<PageTransition><CustomTour /></PageTransition>} />
          <Route path="/when-to-visit" element={<PageTransition><WhenToVisit /></PageTransition>} />
          <Route path="/hotels" element={<PageTransition><Hotels /></PageTransition>} />

          {/* Legal & Static Pages */}
          <Route path="/privacy-policy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="/terms-and-conditions" element={<PageTransition><Terms /></PageTransition>} />
          <Route path="/cookie-policy" element={<PageTransition><CookiePolicy /></PageTransition>} />
          <Route path="/careers" element={<PageTransition><Careers /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
          <Route path="/sitemap" element={<PageTransition><Sitemap /></PageTransition>} />

          {/* Redirects & Fallbacks */}
          <Route path="/my-tour/" element={<Navigate to="/find-my-tour" />} />
        </Routes>
      </AnimatePresence>

      {!hideFooter && <Newsletter />}
      {!hideFooter && <Footer />}
      <CookieBanner />
    </div>
  )
}

export default App
